import type { TemplateVariable } from '../interfaces/template-variable.interface';
import { templateEditorLogger } from '../logger.instance';
import { SELECTORS, CLASSES } from '../utils/constants';
import { escapeHtml } from '../utils/escape-html';
import { getIconForType } from '../utils/icon-helper';
import type {
  MustacheStatement,
  Expression,
  PathExpression,
  SubExpression,
  Literal,
  Statement,
} from './ast-node.model';
import { PathExpressionNode } from './path-expression';
import { ArgumentParser } from './utils/argument-parser';
import { DomHelpers } from './utils/dom-helpers';

const logger = templateEditorLogger.extend('mustache-statement');

/**
 * Um alias de tipo para a função que constrói uma sub-árvore AST a partir de um nó do DOM.
 */
type AstBuilder = (node: Node) => Statement[];

/**
 * Implementação do nó `MustacheStatement` da AST.
 * Representa expressões como `{{variavel}}` ou `{{funcao arg1 "arg2"}}`.
 * Contém a lógica para converter entre a representação AST, o DOM ("chip") e a string HTML.
 */
export class MustacheStatementNode implements MustacheStatement {
  public readonly type = 'MustacheStatement';

  public escaped = true; // Por padrão, a saída é escapada em Handlebars.

  public constructor(
    public path: PathExpression,
    public params: Expression[],
  ) {}

  /**
   * Converte o nó AST de volta para a sintaxe de string do Handlebars.
   */
  public toString(): string {
    logger.debug('toString', { path: this.path, params: this.params });

    const paramsString = this.params.map((param) => param.toString()).join(' ');
    const result = `{{${this.path.toString()}${paramsString ? ' ' + paramsString : ''}}}`;
    logger.debug('toString result', { result });
    return result;
  }

  /**
   * Analisa um elemento "chip" do DOM para construir um `MustacheStatementNode`.
   *
   * @param element O elemento `<span>` wrapper do chip.
   * @param astBuilder Uma função para construir ASTs de nós filhos (usado para argumentos).
   * @param knownVariables Um mapa de variáveis conhecidas para ajudar na diferenciação entre paths e literais.
   * @returns A instância de `MustacheStatementNode` ou `null` se o parsing falhar.
   */
  public static parse(
    element: HTMLElement,
    astBuilder: AstBuilder,
    knownVariables: Map<string, TemplateVariable>,
  ): MustacheStatementNode | null {
    logger.debug('parse called', { element, knownVariables });
    if (!element.matches(SELECTORS.CHIP_WRAPPER)) return null;
    logger.debug('Parsing MustacheStatementNode from element:', element);

    const chip = element.querySelector<HTMLElement>(
      `${SELECTORS.PATH}, ${SELECTORS.FUNCTION_NAME}`,
    );
    logger.debug('Found chip element:', chip);
    if (!chip) return null;

    const pathString = chip.dataset['path'] ?? chip.dataset['functionName'];
    logger.debug('Extracted path/function name:', pathString);
    if (!pathString) return null;

    const path = new PathExpressionNode(pathString);
    const params: Expression[] = [];

    // Se for uma função, parseia seus argumentos.
    if (chip.dataset['functionName']) {
      params.push(...ArgumentParser.parseArguments(chip, astBuilder, knownVariables));
    }

    const node = new MustacheStatementNode(path, params);
    logger.debug('parse: created MustacheStatementNode', { node });
    return node;
  }

  /**
   * Cria um nó do DOM (um "chip") a partir de uma definição de `TemplateVariable`.
   *
   * @param variable A variável a ser convertida em um chip.
   * @returns O nó do DOM (`<span>`) representando o chip.
   */
  public static toDomNode(variable: TemplateVariable): Node {
    logger.debug('toDomNode', { variable });
    let node: Node;
    if (variable.type === 'function' && variable.args) {
      node = this.createFunctionDomNode(variable);
    } else {
      node = this.createVariableDomNode(variable);
    }

    logger.debug('toDomNode result', { node });
    return node;
  }

  /**
   * Cria o botão de exclusão para os chips.
   */
  private static createDeleteButton(): HTMLElement {
    logger.debug('createDeleteButton called');
    return DomHelpers.createDeleteButton('chip-wrapper__delete-button');
  }

  /**
   * Cria o DOM para um chip de variável simples.
   */
  private static createVariableDomNode(variable: TemplateVariable): Node {
    const wrapper = document.createElement('span');
    wrapper.className = `chip-wrapper ${CLASSES.CHIP_ENTER}`;
    wrapper.contentEditable = 'false';

    const { icon, chipClass } = getIconForType(variable.type);

    const chip = document.createElement('span');
    chip.className = `chip-wrapper__chip ${chipClass}`;
    chip.dataset['path'] = variable.path;

    // Insere o ícone SVG.
    chip.innerHTML = icon;
    chip.append(document.createTextNode(variable.label));

    const tooltip = document.createElement('span');
    tooltip.className = 'chip-wrapper__tooltip template-tooltip';
    tooltip.textContent = variable.path;

    wrapper.append(chip, tooltip, this.createDeleteButton());
    return wrapper;
  }

  /**
   * Cria o DOM para um chip de função, incluindo placeholders para os argumentos.
   */
  private static createFunctionDomNode(func: TemplateVariable): Node {
    const wrapper = document.createElement('span');
    wrapper.className = `chip-wrapper ${CLASSES.CHIP_ENTER}`;
    wrapper.contentEditable = 'false';

    const { icon, chipClass } = getIconForType(func.type);

    const chip = document.createElement('span');
    chip.className = `chip-wrapper__chip chip-wrapper__chip--function ${chipClass}`;
    chip.dataset['functionName'] = func.path;

    chip.innerHTML = icon;

    const nameSpan = document.createElement('span');
    nameSpan.className = 'chip-wrapper__chip__name';
    nameSpan.textContent = func.path;
    chip.append(nameSpan);

    chip.append(document.createTextNode('('));

    // Cria um span editável para cada argumento.
    for (const [index, arg] of func.args!.entries()) {
      if (index > 0) chip.append(document.createTextNode(',\u00A0')); // Vírgula e non-breaking space

      const argWrapper = document.createElement('span');
      argWrapper.className = `${CLASSES.ARG_WRAPPER} chip-wrapper__arg-wrapper`;

      const argSpan = document.createElement('span');
      argSpan.className = `${CLASSES.ARG_PLACEHOLDER} chip-wrapper__arg-placeholder`;
      argSpan.dataset['argName'] = arg.name;
      argSpan.contentEditable = 'true';

      const defaultValue = arg.defaultValue?.toString();
      argSpan.textContent = defaultValue ?? arg.name;
      if (!defaultValue) {
        argSpan.classList.add(CLASSES.IS_PLACEHOLDER);
      }

      const tooltip = document.createElement('span');
      tooltip.className = 'chip-wrapper__tooltip template-tooltip';
      tooltip.textContent = arg.description;

      argWrapper.append(argSpan, tooltip);
      chip.append(argWrapper);
    }

    chip.append(document.createTextNode(')'));
    wrapper.append(chip);
    wrapper.append(this.createDeleteButton());
    return wrapper;
  }

  /**
   * Converte um `MustacheStatement` da AST para sua representação em string HTML para o editor.
   *
   * @param statement O nó da AST a ser renderizado.
   * @param knownVariables O mapa de variáveis para obter metadados (label, tipo, etc.).
   * @returns A string HTML do chip.
   */
  public static toHtml(
    statement: MustacheStatement,
    knownVariables: Map<string, TemplateVariable>,
  ): string {
    logger.debug('toHtml called', { statement, knownVariables });
    const definition = knownVariables.get(statement.path.original);
    // Se a variável não for conhecida e não for uma @data variable, renderiza como texto simples.
    if (!definition && !statement.path.data) {
      const result = escapeHtml(statement.toString());
      logger.debug('toHtml: unknown variable, rendering as plain text', { result });
      return result;
    }

    const variable =
      definition ??
      ({
        path: statement.path.original,
        label: statement.path.original,
        type: 'variable',
      } as TemplateVariable);

    let html: string;
    if (variable.type === 'function' && variable.args) {
      html = this.renderFunctionToHtml(variable, statement, knownVariables);
    } else {
      html = this.renderVariableToHtml(variable);
    }

    logger.debug('toHtml result', { html });
    return html;
  }

  /**
   * Renderiza o HTML do botão de exclusão.
   */
  private static renderDeleteButtonHtml(): string {
    return DomHelpers.renderDeleteButtonHtml('chip-wrapper__delete-button');
  }

  /**
   * Renderiza o HTML de um chip de variável.
   */
  private static renderVariableToHtml(variable: TemplateVariable): string {
    const label = escapeHtml(variable.label);
    const path = escapeHtml(variable.path);
    const { icon, chipClass } = getIconForType(variable.type);
    const chipHtml = `<span class="chip-wrapper__chip ${chipClass}" data-path="${path}">${icon}${label}</span>`;
    const tooltipHtml = `<span class="chip-wrapper__tooltip template-tooltip">${path}</span>`;

    return `<span class="chip-wrapper" contenteditable="false">${chipHtml}${tooltipHtml}${this.renderDeleteButtonHtml()}</span>`;
  }

  /**
   * Renderiza o HTML de um chip de função, preenchendo os argumentos com os valores da AST.
   */
  private static renderFunctionToHtml(
    func: TemplateVariable,
    statement: MustacheStatement,
    knownVariables: Map<string, TemplateVariable>,
  ): string {
    const funcName = escapeHtml(func.path);
    const { icon, chipClass } = getIconForType(func.type);

    const argsHtml = func
      .args!.map((arg, index) => {
        const param = statement.params[index];
        let valueHtml: string;
        let isPlaceholder = false;

        // Renderiza o valor do argumento com base em seu tipo na AST.
        if (param?.type.endsWith('Literal')) {
          valueHtml = escapeHtml(String((param as Literal).value));
        } else if (param?.type === 'PathExpression') {
          const variableDef = knownVariables.get((param as PathExpression).original);
          if (variableDef) {
            // Se for uma variável conhecida, renderiza como um chip aninhado.
            valueHtml = this.renderVariableToHtml(variableDef);
          } else {
            valueHtml = escapeHtml((param as PathExpression).original);
          }
        } else if (param?.type === 'SubExpression') {
          // Se for uma função aninhada, renderiza recursivamente.
          const subStatement: MustacheStatement = {
            type: 'MustacheStatement',
            path: (param as SubExpression).path,
            params: (param as SubExpression).params,
            escaped: true,
            toString: param.toString.bind(param),
          };
          valueHtml = MustacheStatementNode.toHtml(subStatement, knownVariables);
        } else {
          // Se não houver parâmetro na AST, usa o valor padrão ou o nome do argumento.
          valueHtml = escapeHtml(arg.defaultValue?.toString() ?? arg.name);
          if (!arg.defaultValue) isPlaceholder = true;
        }

        const argName = escapeHtml(arg.name);
        const argDesc = escapeHtml(arg.description);
        const customClasses = isPlaceholder ? CLASSES.IS_PLACEHOLDER : '';
        logger.debug('renderFunctionToHtml: arg', { arg, param, valueHtml, isPlaceholder });

        return (
          `<span class="${CLASSES.ARG_WRAPPER} chip-wrapper__arg-wrapper">` +
          `<span class="${CLASSES.ARG_PLACEHOLDER} chip-wrapper__arg-placeholder ${customClasses}" data-arg-name="${argName}" contenteditable="true">${valueHtml}</span>` +
          `<span class="chip-wrapper__tooltip template-tooltip">${argDesc}</span>` +
          `</span>`
        );
      })
      .join(',&nbsp;');

    const chipContent = `${icon}<span class="chip-wrapper__chip__name">${funcName}</span><span>(</span>${argsHtml}<span>)</span>`;
    const chipHtml = `<span class="chip-wrapper__chip chip-wrapper__chip--function ${chipClass}" data-function-name="${funcName}">${chipContent}</span>`;

    return `<span class="chip-wrapper" contenteditable="false">${chipHtml}${this.renderDeleteButtonHtml()}</span>`;
  }
}
